import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, getOrCreateUserId } from "../lib/api";
import { sfx } from "../lib/sound";
import { hapticSuccess, vibrate } from "../lib/native";
import { showRewardedAd } from "../lib/ads";

// Visual constants for the 7-day strip
const DAY_LABELS = ["DAY 1", "DAY 2", "DAY 3", "DAY 4", "DAY 5", "DAY 6", "DAY 7"];

export default function DailyStreak({ open, onClose, onClaimed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [reward, setReward] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReward(null);
    setLoading(true);
    (async () => {
      try {
        const uid = getOrCreateUserId();
        const res = await axios.get(`${API}/streak/${uid}`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [open]);

  // Two paths: free claim (base reward) OR watch-ad (2× reward).
  const claim = async (watched_ad) => {
    if (!data?.can_claim || claiming) return;
    setClaiming(true);
    try {
      // Watch-ad path: play the rewarded ad first; only credit on success.
      if (watched_ad) {
        try { await showRewardedAd("chips_50"); } catch (adErr) {
          console.warn("streak ad failed:", adErr);
        }
      }
      const uid = getOrCreateUserId();
      const res = await axios.post(`${API}/streak/claim`, { user_id: uid, watched_ad });
      setReward(res.data.reward);
      setData((d) => ({ ...d, can_claim: false, current_streak: res.data.current_streak, last_claim_at: d.today }));
      sfx.win();
      hapticSuccess();
      vibrate([0, 40, 30, 60]);
      if (onClaimed) onClaimed(res.data.profile);
    } catch (e) { console.error(e); }
    setClaiming(false);
  };

  if (!open) return null;

  const cur = data?.current_streak || 0;
  const next = data?.next_streak || 1;
  // Highlight index = day cycle position (0-indexed)
  const cyclePos = data ? ((data.can_claim ? next : cur) - 1) % 7 : 0;
  const rewards = data?.rewards || [];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(0,0,0,0.78)" }}
        data-testid="streak-modal"
      >
        <motion.div
          initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }}
          className="glass max-w-2xl w-full p-6 md:p-8 rounded-3xl"
          style={{ maxHeight: "92vh", overflowY: "auto" }}
          style={{ borderColor: "rgba(255,215,0,0.4)" }}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="label-eyebrow" style={{ color: "#FFD700" }}>Daily Streak</div>
            <button className="btn-ghost" style={{ padding: "6px 14px", fontSize: 11 }} onClick={onClose} data-testid="streak-close-btn">Close</button>
          </div>
          <h2 className="font-display font-black text-3xl md:text-4xl mb-1">
            {data?.can_claim ? "Today's Reward" : (reward != null ? `+${reward} chips!` : "Come back tomorrow")}
          </h2>
          <p className="text-white/65 text-sm mb-5">
            {data?.can_claim
              ? "Claim every day to grow your streak. Miss a day and it resets."
              : `Next claim resets in under 24h. Current streak: ${cur} day${cur === 1 ? "" : "s"}.`}
          </p>

          {loading ? (
            <div className="text-center py-8 text-white/50">Loading…</div>
          ) : (
            <div className="grid grid-cols-7 gap-2 mb-5" data-testid="streak-strip">
              {DAY_LABELS.map((label, i) => {
                const isHit = i < cyclePos || (!data?.can_claim && i === cyclePos);
                const isCurrent = i === cyclePos && data?.can_claim;
                const reward_i = rewards[i] ?? 0;
                const isJackpot = i === 6;
                return (
                  <div
                    key={i}
                    className="rounded-xl p-2 text-center relative"
                    style={{
                      background: isCurrent
                        ? "linear-gradient(155deg, #FFD700, #FF9D00)"
                        : (isHit ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.04)"),
                      border: `1px solid ${isCurrent ? "#FFD700" : (isHit ? "rgba(255,215,0,0.4)" : "rgba(255,255,255,0.1)")}`,
                      boxShadow: isCurrent ? "0 0 24px rgba(255,215,0,0.55)" : (isJackpot ? "inset 0 0 8px rgba(255,215,0,0.25)" : "none"),
                      color: isCurrent ? "#0B0C10" : "#fff",
                    }}
                    data-testid={`streak-day-${i + 1}`}
                  >
                    <div className="label-eyebrow" style={{ fontSize: 8, color: isCurrent ? "#0B0C10" : "rgba(255,255,255,0.6)" }}>
                      {label}
                    </div>
                    <div className="font-mono font-bold mt-1" style={{ fontSize: 14 }}>
                      {reward_i}
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.85 }}>◆</div>
                    {isHit && !isCurrent && (
                      <div style={{ position: "absolute", top: 4, right: 4, color: "#FFD700", fontSize: 10 }}>✓</div>
                    )}
                    {isJackpot && (
                      <div className="label-eyebrow absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full"
                        style={{ fontSize: 7, background: "#FFD700", color: "#0B0C10" }}>BONUS</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            {data?.can_claim ? (
              <div className="flex gap-2 w-full justify-center flex-wrap">
                <button
                  className="btn-ghost"
                  onClick={() => claim(false)}
                  disabled={claiming}
                  data-testid="streak-claim-btn"
                >
                  {claiming ? "Claiming…" : `Claim ${data.next_reward} ◆`}
                </button>
                <button
                  className="btn-primary flex items-center gap-2"
                  onClick={() => claim(true)}
                  disabled={claiming}
                  data-testid="streak-claim-ad-btn"
                  style={{
                    background: "linear-gradient(135deg, #3DDC97, #00E5FF)",
                    color: "#0B0C10",
                    fontWeight: 800,
                  }}
                >
                  <span>📺</span>
                  {claiming ? "Loading…" : `Watch · ${data.next_reward * 2} ◆`}
                </button>
              </div>
            ) : (
              <button className="btn-ghost" onClick={onClose} data-testid="streak-okay-btn">
                Okay
              </button>
            )}
            <div className="label-eyebrow" style={{ fontSize: 10 }} data-testid="streak-info">
              Streak {cur}{data?.longest_streak ? ` · Longest ${data.longest_streak}` : ""}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
